import { BEHAVIORS } from '@wix/wix-data-client-dbsm-common/dist/esm/connection-config/behaviors'
import type { EditorSDK } from '@wix/wix-data-client-common-standalone'
import { TEXT_ROLE } from '@wix/wix-data-client-common-standalone'
import type { EDITOR_TYPE } from '@wix/wix-data-client-common'
import { isStudio, isBlocks } from '@wix/wix-data-client-common'
import type { ComponentRef } from '@wix/editor-platform-sdk-types'
import type { BehaviorType } from '@wix/data-binding-registry-sdk'

type Params = {
  editorSdkProxy: EditorSDK
  behaviorType: BehaviorType
  controllerRef: ComponentRef
  componentRef: ComponentRef
  editorType: EDITOR_TYPE
}

export const addSubmitFeedbackMessage = async ({
  editorSdkProxy,
  behaviorType,
  controllerRef,
  componentRef,
  editorType,
}: Params) => {
  const isBlocksEditor = isBlocks(editorType)
  const isStudioEditor = isStudio(editorType)

  const getComponentDefintion = async () => {
    return editorSdkProxy.components.serialize({
      componentRef,
      maintainIdentifiers: true,
    })
  }

  const containerRef = await getContainerRef({
    editorSdkProxy,
    isBlocksEditor,
    isStudioEditor,
    componentRef,
  })

  const textRef = await editorSdkProxy.components.add({
    pageRef: containerRef,
    componentDefinition: {
      componentType: 'wysiwyg.viewer.components.WRichText',
      data: {
        text: BEHAVIORS[behaviorType].textElement,
      },
      props: { isHidden: true, type: 'WRichTextProperties' },
      layout: { width: 250 },
      ...((isStudioEditor || isBlocksEditor) && {
        layouts: {
          ...(isBlocksEditor && {
            componentLayout: {
              type: 'ComponentLayout',
              height: { type: 'auto' },
              width: { type: 'px', value: 238 },
            },
          }),
          itemLayout: {
            ...(await getComponentDefintion()).layouts.itemLayout,
            alignSelf: 'center',
            justifySelf: 'center',
            margins: {
              left: {
                type: 'px',
                value: 0,
              },
              top: {
                type: 'px',
                value: 0,
              },
            },
          },
        },
      }),
    },
  })

  const appApi = await editorSdkProxy.editor.getAppAPI()
  await appApi.connectBindingSection({
    componentRef: textRef,
    controllerRef,
    newBindings: [
      {
        behavior: behaviorType,
        type: 'Behavior',
      },
    ],
    role: TEXT_ROLE,
    requiresPrimaryConnection: true,
  })
}

async function getContainerRef({
  componentRef,
  editorSdkProxy,
  isBlocksEditor,
  isStudioEditor,
}: {
  componentRef: ComponentRef
  editorSdkProxy: EditorSDK
  isBlocksEditor: boolean
  isStudioEditor: boolean
}) {
  const [pageRef, ancestors] = await Promise.all([
    editorSdkProxy.components.getPage({ componentRef }),
    editorSdkProxy.components.getAncestors({
      componentRef,
    }),
  ])

  if (!isBlocksEditor && !isStudioEditor) {
    return pageRef
  }

  const hostComponentRef =
    await editorSdkProxy.components.refComponents.getHostComponentRef({
      componentRef,
    })

  if (hostComponentRef) {
    const widgetRefIndex = ancestors.findIndex(
      ancestor => ancestor.id === hostComponentRef.id,
    )
    const nonWidgetAncestors = ancestors.slice(widgetRefIndex + 1)
    return nonWidgetAncestors[0]
  }
  return ancestors[0]
}
